export const SALE_PRODUCT_SECTORS = ["Cocina", "Barra"];

export const UNIT_LABELS = {
  g: "g",
  ml: "ml",
  unit: "unidad",
  package: "paquete",
  portion: "porción",
};

const QUANTITY_PATTERN = /^\d+(?:[.,]\d{1,4})?$/;
const PRICE_PATTERN = /^\d+(?:[.,]\d{1,2})?$/;

export function componentKey(component) {
  return `${component.kind}:${Number(component.id)}`;
}

export function normalizeDecimalInput(value) {
  return String(value ?? "").replace(/\./g, ",");
}

export function formatDecimalInput(value) {
  const normalized = normalizeDecimalInput(value).trim();
  if (!/^\d+(?:,\d+)?$/.test(normalized)) return normalized;
  const [integer, decimals = ""] = normalized.split(",");
  const significant = decimals.replace(/0+$/, "");
  return significant ? `${integer},${significant}` : integer;
}

export function parseQuantity(value) {
  const normalized = String(value ?? "").trim().replace(",", ".");
  if (!QUANTITY_PATTERN.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0.0001 && parsed <= 99999999.9999
    ? parsed
    : null;
}

export function parsePrice(value) {
  const normalized = String(value ?? "").trim().replace(",", ".");
  if (!PRICE_PATTERN.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 9999999999.99
    ? parsed
    : null;
}

export function normalizeSectors(sectors) {
  const values = Array.isArray(sectors) ? sectors : String(sectors || "").split(",");
  return SALE_PRODUCT_SECTORS.filter((sector) =>
    values.some(
      (value) =>
        String(value).trim().toLocaleLowerCase("es-AR") ===
        sector.toLocaleLowerCase("es-AR")
    )
  );
}

export function buildCompositionPayload(components) {
  const line = (component, idKey) => ({
    [idKey]: Number(component.id),
    quantity: parseQuantity(component.quantity),
  });

  return {
    ingredients: components
      .filter((component) => component.kind === "ingredient")
      .map((component) => line(component, "ingredientId")),
    preparedProducts: components
      .filter((component) => component.kind === "preparedProduct")
      .map((component) => line(component, "preparedProductId")),
    recipes: components
      .filter((component) => component.kind === "recipe")
      .map((component) => line(component, "recipeId")),
  };
}

export function buildSaleProductPayload({ name, sector, price, components }) {
  return {
    name: String(name ?? "").trim(),
    sector: normalizeSectors(sector),
    price: parsePrice(price),
    ...buildCompositionPayload(components),
  };
}

export function canonicalFormSignature({ name, sector, price, components }) {
  const normalizedComponents = components
    .map((component) => ({
      kind: component.kind,
      id: Number(component.id),
      quantity: formatDecimalInput(component.quantity),
    }))
    .sort((left, right) => componentKey(left).localeCompare(componentKey(right)));
  return JSON.stringify({
    name: String(name ?? "").trim(),
    sector: normalizeSectors(sector),
    price: formatDecimalInput(price),
    components: normalizedComponents,
  });
}

export function previewSignature(components) {
  return JSON.stringify(buildCompositionPayload(components));
}

export function formatCurrency(value) {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

export function formatQuantity(value) {
  return formatDecimalInput(value) || "0";
}

export function rowsFromResponse(response) {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response)) return response;
  return [];
}

export function mapDetailToInitialValues(product) {
  const ingredients = Array.isArray(product?.ingredients) ? product.ingredients : [];
  const preparedProducts = Array.isArray(product?.preparedProducts)
    ? product.preparedProducts
    : [];
  const recipes = Array.isArray(product?.recipes) ? product.recipes : [];

  return {
    name: product?.name || "",
    sector: normalizeSectors(product?.sector),
    price: formatDecimalInput(product?.price),
    components: [
      ...ingredients.map((item) => ({
        kind: "ingredient",
        group: "Insumos",
        id: Number(item.ingredientId),
        name: item.name || `Insumo #${item.ingredientId}`,
        unit: item.unit || "",
        unitLabel: UNIT_LABELS[item.unit] || item.unit || "sin unidad",
        quantity: formatDecimalInput(item.quantity),
        isAvailable: true,
      })),
      ...preparedProducts.map((item) => ({
        kind: "preparedProduct",
        group: "Elaborados",
        id: Number(item.preparedProductId),
        name: item.name || `Elaborado #${item.preparedProductId}`,
        unit: item.resultUnit || "",
        unitLabel:
          UNIT_LABELS[item.resultUnit] || item.resultUnit || "sin unidad",
        quantity: formatDecimalInput(item.quantity),
        isAvailable: true,
      })),
      ...recipes.map((item) => ({
        kind: "recipe",
        group: "Recetas",
        id: Number(item.recipeId),
        name: item.name || `Receta #${item.recipeId}`,
        unit: "portion",
        unitLabel: "porciones",
        quantity: formatDecimalInput(item.quantity),
        isAvailable: true,
      })),
    ],
    resolvedIngredients: product?.resolvedIngredients || [],
    cost: product?.cost ?? null,
    costStatus: product?.costStatus,
    costReviewReasons: product?.costReviewReasons || [],
  };
}
